/**
 * Cloud export OAuth in a popup: /oauth-start → Google → /export-callback.
 * Callback posts to opener via window.postMessage; this file listens and resolves.
 */

import { getConnections, initiateConnect } from '@/services/export'
import type { ExportProvider } from '@/types/export'

const POPUP_NAME = 'dday-export-oauth'
const OAUTH_MESSAGE_TYPE = 'oauth-callback'
const OAUTH_CHANNEL_PREFIX = 'dday-export-oauth'
const CONNECTION_VERIFY_ATTEMPTS = 6
const CONNECTION_VERIFY_INTERVAL_MS = 500

export type ExportOAuthResult =
  | { success: true; email?: string }
  | { success: false; error: string }

/** Popup closed before OAuth finished — callers often skip error toasts. */
export const EXPORT_OAUTH_CANCELLED = 'Connection cancelled'

async function detectConnectedProvider(
  provider: ExportProvider
): Promise<{ connected: true; email?: string } | { connected: false }> {
  // Fallback for browsers/environments where popup callback messaging is blocked:
  // poll the canonical backend connection list briefly before treating as cancelled.
  for (let attempt = 0; attempt < CONNECTION_VERIFY_ATTEMPTS; attempt++) {
    try {
      const response = await getConnections()
      const match = response.data.connections.find(
        (c) => c.provider === provider
      )
      if (match) {
        return { connected: true, email: match.email || undefined }
      }
    } catch {
      // Ignore transient failures while reconciling the OAuth callback.
    }

    if (attempt < CONNECTION_VERIFY_ATTEMPTS - 1) {
      await new Promise((resolve) =>
        window.setTimeout(resolve, CONNECTION_VERIFY_INTERVAL_MS)
      )
    }
  }

  return { connected: false }
}

export async function connectExportProviderInPopup(
  provider: ExportProvider
): Promise<ExportOAuthResult> {
  const flowId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  // Must open synchronously in the user gesture; await before open and the browser blocks the popup.
  const popup = window.open(
    'about:blank',
    POPUP_NAME,
    'width=520,height=720,scrollbars=yes,resizable=yes'
  )

  if (!popup) {
    return {
      success: false,
      error: 'Popup blocked — allow popups for this site',
    }
  }

  let authUrl: string
  try {
    const returnUrl = new URL('/export-callback', window.location.origin)
    returnUrl.searchParams.set('flowId', flowId)
    const response = await initiateConnect(provider, returnUrl.toString())
    authUrl = response.data.authUrl
  } catch {
    popup.close()
    return { success: false, error: 'Failed to start connection' }
  }

  const startUrl = new URL('/oauth-start', window.location.origin)
  startUrl.searchParams.set('authUrl', encodeURIComponent(authUrl))
  popup.location.href = startUrl.toString()

  return await new Promise<ExportOAuthResult>((resolve) => {
    let settled = false
    // Prevent overlapping verification loops when closed polling fires multiple times.
    let verifyingConnection = false
    const channelName = `${OAUTH_CHANNEL_PREFIX}:${flowId}`
    const channel =
      typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(channelName)
        : null

    const parseOAuthPayload = (raw: unknown) => {
      const data = raw as {
        type?: string
        flowId?: string
        success?: boolean
        email?: string
        error?: string
      }
      if (data?.type !== OAUTH_MESSAGE_TYPE) return null
      // Backward-compatible: accept callback payloads without flowId.
      // Reject only when a different non-empty flowId is provided.
      if (data.flowId && data.flowId !== flowId) return null
      if (data.success) {
        return { success: true as const, email: data.email }
      }
      return {
        success: false as const,
        error: data.error || 'Connection failed',
      }
    }

    const finish = (result: ExportOAuthResult) => {
      if (settled) return
      settled = true
      window.clearInterval(poll)
      window.removeEventListener('message', onMessage)
      channel?.close()
      resolve(result)
    }

    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return
      if (ev.source && ev.source !== popup) return
      const parsed = parseOAuthPayload(ev.data)
      if (!parsed) return
      finish(parsed)
    }

    const onChannelMessage = (ev: MessageEvent) => {
      const parsed = parseOAuthPayload(ev.data)
      if (!parsed) return
      finish(parsed)
    }

    window.addEventListener('message', onMessage)
    if (channel) {
      channel.addEventListener('message', onChannelMessage)
    }

    const poll = window.setInterval(() => {
      if (settled) return
      let isClosed = false
      try {
        // Cross-origin popup phases can throw here under COOP.
        // Treat as "not closed yet" and wait for callback message/channel.
        isClosed = popup.closed
      } catch {
        return
      }
      if (!isClosed) return
      window.setTimeout(async () => {
        if (settled || verifyingConnection) return
        verifyingConnection = true
        const connectionState = await detectConnectedProvider(provider)
        if (settled) return
        if (connectionState.connected) {
          finish({ success: true, email: connectionState.email })
          return
        }
        finish({ success: false, error: EXPORT_OAUTH_CANCELLED })
      }, 700)
    }, 400)
  })
}
